import Swal from 'sweetalert2';
import Helper from './helper';

const ErrorHelper = {

  async handleHttpStatus(status, errorData = {}) {
    switch(status) {
      case 400:
        return this.handleBadRequest(errorData);
      case 401:
      case 403:
        return this.handleAuthError(status);
      case 404:
        return this.handleNotFound(errorData);
      case 422:
         return this.handleUnprocessableEntity(errorData);
      case 429:
        return this.handleRateLimit();
      case 500:
        return this.handleServerError();
      default:
        return this.handleGenericError(status, errorData);
    }
  },

  async handleUnprocessableEntity(data) {


    const displayData = {
      title: data.title || 'Erreur de validation',
      message: data.msg ||  data.message  || 'Données invalides',
      errors: data.errors || {},
      ...this.extractMetadata(data)
    };

    await this.showAlert({
      ...displayData,
      icon: 'error',
      html: this.buildValidationHtml(displayData),
      confirmButtonText: 'Compris'
    });

    return {
      type: 'validation',
      fields: displayData.errors,
      shouldRetry: false,
      ...displayData
    };
  },

  buildValidationHtml(data) {
    if (!data.errors || Object.keys(data.errors).length === 0) {
      return `<p>${data.message}</p>`;
    }

    const errorsList = Object.entries(data.errors)
      .map(([field, errors]) => {
        const fieldName = this.humanizeFieldName(field);
        const errorsText = Array.isArray(errors) ? errors?.join(', ') : errors;
        return `<li><strong>${fieldName}:</strong> ${errorsText}</li>`;
      })
      .join('');

    return `
      <p>${data.message}</p>
      <ul class="validation-errors">${errorsList}</ul>
    `;
  },





  // Gestion spécifique pour chaque type d'erreur
  async handleBadRequest(data) {
    await this.showValidationErrors(data.validations || {});
    return { 
      type: 'validation',
      shouldRetry: false
    };
  },

  async handleAuthError(status) {
    localStorage.removeItem(Helper.CLIENT_TOKEN);
    await Swal.fire({
      title: status === 401 ? 'Session expirée' : 'Accès refusé',
      text: status === 401 
        ? 'Veuillez vous reconnecter' 
        : 'Vous ne disposez pas des droits nécessaires',
      icon: 'warning',
      willClose: () => {
        console.log("redirect is called")
        Helper.redirect(Helper.route('logout', null, false));
      }
    });
    return { type: 'auth', shouldRetry: false };
  },

  async handleNotFound(data) {
    await Swal.fire({
      title: 'Ressource introuvable',
      html: `
        <p>${data.msg || 'La page demandée n\'existe pas'}</p>
        ${data.url ? `<small>URL: ${data.url}</small>` : ''}
      `,
      icon: 'error',
      confirmButtonText: 'OK'
    });
    return { type: 'not_found', shouldRetry: false };
  },

  async handleRateLimit() {
    const result = await Swal.fire({
      title: 'Trop de requêtes',
      text: 'Veuillez patienter avant de réessayer',
      icon: 'warning',
      timer: 5000,
      showConfirmButton: true
    });
    return { type: 'rate_limit', shouldRetry: result.isConfirmed };
  },

  async handleServerError() {
    await Swal.fire({
      title: 'Erreur serveur',
      text: 'Problème technique, notre équipe a été notifiée',
      icon: 'error',
      confirmButtonText: 'OK'
    });
    return { type: 'server', shouldRetry: true };
  },

  async handleGenericError(status, data) {
    await Swal.fire({
      title: `Erreur ${status}`,
      text: data.msg || 'Une erreur inattendue est survenue',
      icon: 'question',
      confirmButtonText: 'Compris'
    });
    return { type: 'generic', shouldRetry: false };
  },

  /**
   * Affiche les erreurs de validation détaillées
   */
  async showValidationErrors(validations) {
    let html = '<ul class="text-left">';
    Object.entries(validations).forEach(([field, errors]) => {
      html += `<li><strong>${field}:</strong> ${errors?.join(', ')}</li>`;
    });
    html += '</ul>';

    await Swal.fire({
      title: 'Erreur de formulaire',
      html,
      icon: 'error',
      confirmButtonText: 'Corriger'
    });
  },



  /**
   * Gère et affiche les erreurs API avec SweetAlert 2
   * @param {Response} response - Réponse fetch
   * @returns {Promise<Object>} - Objet d'erreur
   */
  async handleAndShowApiError(response) {
    const error = await this.handleApiError(response);
    await this.showErrorWithSweetAlert(error);
    return error;
  },

  humanizeFieldName(field) {
    return field
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  },


  buildErrorFooter(data) {
    if (!data.timestamp && !data.reference) return;
    
    let footer = '';
    if (data.timestamp) footer += `Time: ${new Date(data.timestamp).toLocaleString()}`;
    if (data.reference) footer += ` | Ref: ${data.reference}`;
    return footer;
  },


  /**
   * Traite les erreurs API
   */
  async handleApiError(response) {
    const status = response.status;

    try {
      const errorData = await response.json();

      this.handleHttpStatus(status, errorData);

    } catch (parseError) {
      return this.handleUnexpectedError(parseError);
    }
  },

  /**
   * Gère les erreurs inattendues
   */
  handleUnexpectedError(error) {
    console.error('Erreur inattendue:', error);
    return {
      type: 'unexpected',
      title: 'Erreur inattendue',
      message: 'Une erreur technique est survenue',
      icon: 'error',
      footer: error.message
    };
  },

  /**
   * Affiche l'erreur avec SweetAlert 2
   */
  async showErrorWithSweetAlert(error) {
    if (!error) return;
    
    await Swal.fire({
      title: error.title || 'Erreur',
      html: this.buildErrorHtml(error),
      icon: error.icon || 'error',
      footer: error.footer,
      confirmButtonText: 'OK',
      confirmButtonColor: '#3085d6'
    });
    
    // Si c'est une erreur de validation, on peut les logger
    if (error.type === 'validation') {
      console.error('Erreurs de validation:', error.errors);
    }
  },

  /**
   * Construit le HTML pour l'alerte
   */
  buildErrorHtml(error) {
    let html = `<p>${error.message}</p>`;
    
    if (error.errors) {
      html += '<ul class="text-left">';
      for (const [field, messages] of Object.entries(error.errors)) {
        html += `<li><strong>${field}:</strong> ${messages.join(', ')}</li>`;
      }
      html += '</ul>';
    }
    
    return html;
  },

  /**
   * Détermine l'icône en fonction du code d'erreur
   */
  getErrorIcon(errorCode) {
    const warningCodes = [
      Helper.ApiErrorCustomCode?.AMOUNT_TOO_LITTLE,
      Helper.ApiErrorCustomCode?.INVALID_METHOD_PHONE_FORMAT
    ];
    return warningCodes.includes(errorCode) ? 'warning' : 'error';
  },

  /**
   * Construit le footer avec des détails techniques
   */
  getErrorFooter(error) {
    if (!error.className && !error.methodName) return;
    
    let footer = '';
    if (error.className) footer += `Class: ${error.className}<br>`;
    if (error.methodName) footer += `Method: ${error.methodName}`;
    return footer;
  },

  /**
   * Messages utilisateur personnalisés
   */
  getUserFriendlyMessage(error) {
    switch(error.customCode) {
      case Helper.ApiErrorCustomCode?.AMOUNT_TOO_LITTLE:
        return 'Le montant est trop faible pour cette opération';
      case Helper.ApiErrorCustomCode?.AMOUNT_TOO_MUCH:
        return 'Le montant dépasse la limite autorisée';
      case Helper.ApiErrorCustomCode?.INVALID_METHOD_PHONE_FORMAT:
        return 'Format de numéro de téléphone invalide';
      case Helper.ApiErrorCustomCode?.ACCESS_TOKEN_EXPIRED:
        return 'Votre session a expiré, veuillez vous reconnecter';
      default:
        return error.msg || 'Une erreur est survenue lors de l\'opération';
    }
  },


   async safeParseResponse(response) {
    try {
      return await response.json();
    } catch {
      return {
        msg: await response.text() || `HTTP Error ${response.status}`,
        statusCode: response.status,
        url: response.url
      };
    }
  },


   async showAlert(config) {
    const defaultConfig = {
      icon: 'error',
      confirmButtonColor: '#3085d6',
      scrollbarPadding: false
    };

    await Swal.fire({ ...defaultConfig, ...config });
  },

  extractMetadata(data) {
    return {
      timestamp: data.timestamp,
      reference: data.reference,
      method: data.methodName,
      service: data.className
    };
  },

  logError(status, errorData) {
    if (process.env.NODE_ENV === 'development') {
      console.groupCollapsed(`[HTTP ${status}] ${errorData.msg || 'Erreur API'}`);
      console.log('Détails:', errorData);
      console.groupEnd();
    }
  },

};

export default ErrorHelper;